/**
 * Cell text extraction for the ASK AI CSV download feature.
 *
 * Turns one rendered `table` element into the rectangular `CellTextGrid` that
 * `serializeCsv.ts` consumes: one grid row per table row, one grid column per
 * column position, `colspan` and `rowspan` expanded, every cell's text read
 * with `textContent` and trimmed (Req 3.1-3.5, 3.7-3.9, 3.20).
 *
 * Pure functions over DOM elements. Nothing here mutates the table, reads
 * `innerHTML`, or touches React state, so the whole module can be exercised
 * against real jsdom tables from a property test.
 */

import { CellTextGrid, CHUNK_ROWS, MAX_SPAN } from "./types";

// ── Errors ─────────────────────────────────────────────────────────────────

/**
 * The cell structure of a table could not be read, so no CSV file can be
 * produced from it (Req 7.1). Raised for a table with no rows and for a table
 * whose every row has no cells; a malformed span is not a structure error,
 * because `clampSpan` degrades it to a sane value instead.
 */
export class TableStructureError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "TableStructureError";
    // Restores the prototype chain when this class is downleveled, so
    // `instanceof TableStructureError` stays reliable in the download hook.
    Object.setPrototypeOf(this, TableStructureError.prototype);
  }
}

// ── Spans ──────────────────────────────────────────────────────────────────

/**
 * A cell's `colspan` or `rowspan` as a usable span count (Req 3.7, 3.8).
 *
 * Returns `1` when the span is absent, zero, negative, or not an integer, and
 * otherwise caps the value at {@link MAX_SPAN}. The cap is a hardening
 * measure: a hostile `colspan="1000000"` must not make the converter allocate
 * an unbounded grid.
 */
export function clampSpan(span: number | null | undefined): number {
  if (span === undefined || span === null) {
    return 1;
  }
  if (!Number.isInteger(span) || span < 1) {
    return 1;
  }
  return Math.min(span, MAX_SPAN);
}

// ── Grid extraction ────────────────────────────────────────────────────────

/**
 * Grid under construction. A slot holds `undefined` until some cell claims
 * it, which is what lets an earlier row's `rowspan` reserve a slot that a
 * later row must skip over. Rows are sparse: a `rowspan` can reach a row
 * index before that row's own cells have been read.
 */
type SparseGrid = (string | undefined)[][];

/** The row at `index`, created empty when it does not exist yet. */
function ensureRow(grid: SparseGrid, index: number): (string | undefined)[] {
  const existing = grid[index];
  if (existing !== undefined) {
    return existing;
  }
  const created: (string | undefined)[] = [];
  grid[index] = created;
  return created;
}

/**
 * Writes one table row's cells into `grid` at row index `r`, expanding each
 * cell over the slots it spans.
 *
 * Each cell's text goes at its first spanned position and every other
 * position it spans gets an empty string (Req 3.7, 3.8). Slots already
 * claimed by an earlier row's `rowspan` are skipped rather than overwritten,
 * which is what keeps a vertically spanned value in one column.
 *
 * A `rowspan` is bounded by the rows that exist below the cell (Req 3.20), so
 * a span reaching past the last row of the table stops at the last row and
 * the grid keeps one record per table row (Req 3.1). `colspan` needs no
 * equivalent bound: the grid's width is whatever the cells claim, so a wide
 * span cannot produce a column that corresponds to nothing.
 *
 * @param rowsTotal Row count of the table being read, which is the bound on
 * how far a `rowspan` may reach. Passed in rather than read off the row's
 * table so the synchronous and the chunked path share one bound.
 */
function claimRowSlots(
  grid: SparseGrid,
  r: number,
  row: HTMLTableRowElement,
  rowsTotal: number
): void {
  const target = ensureRow(grid, r);
  const cells = row.cells;
  let c = 0;

  for (let i = 0; i < cells.length; i += 1) {
    const cell = cells[i];

    // Skip slots claimed by an earlier row's rowspan. Without this the
    // spanned value would be overwritten by this row's next cell.
    while (target[c] !== undefined) {
      c += 1;
    }

    // Req 3.4: cell markup removed, leading and trailing whitespace removed.
    // `textContent` never evaluates markup, unlike `innerHTML`.
    const text = (cell.textContent ?? "").trim();
    const colspan = clampSpan(cell.colSpan);
    // Req 3.20: a `rowspan` covers only the rows that exist, so an
    // overflowing span stops at the last row instead of creating grid rows
    // that correspond to no table row.
    const rowsBelow = Math.max(1, rowsTotal - r);
    const rowspan = Math.min(clampSpan(cell.rowSpan), rowsBelow);

    for (let dr = 0; dr < rowspan; dr += 1) {
      const spanned = dr === 0 ? target : ensureRow(grid, r + dr);
      for (let dc = 0; dc < colspan; dc += 1) {
        spanned[c + dc] = dr === 0 && dc === 0 ? text : "";
      }
    }

    c += colspan;
  }
}

/**
 * Copies a sparse grid into a rectangular `CellTextGrid`, filling every
 * unclaimed slot and every short row with an empty string (Req 3.9).
 *
 * `CellTextGrid` is documented as rectangular and `serializeCsv` relies on
 * that, so this runs before the grid is returned.
 */
function rectangularize(grid: SparseGrid): CellTextGrid {
  let width = 0;
  for (let r = 0; r < grid.length; r += 1) {
    const row = grid[r];
    if (row !== undefined && row.length > width) {
      width = row.length;
    }
  }

  const rectangular: CellTextGrid = [];
  for (let r = 0; r < grid.length; r += 1) {
    const row = grid[r];
    const filled: string[] = [];
    for (let c = 0; c < width; c += 1) {
      filled.push(row?.[c] ?? "");
    }
    rectangular.push(filled);
  }
  return rectangular;
}

/**
 * The rows of a table, or a structure error when it has none (Req 7.1).
 *
 * Shared by the synchronous and the chunked path so the two cannot disagree
 * about what counts as an unreadable table.
 */
function requireRows(
  table: HTMLTableElement
): HTMLCollectionOf<HTMLTableRowElement> {
  const rows = table.rows;
  if (rows.length === 0) {
    throw new TableStructureError("The table has no rows.");
  }
  return rows;
}

/**
 * Turns a finished sparse grid into the returned `CellTextGrid`, rejecting a
 * table whose every row held no cells (Req 3.9, 7.1).
 *
 * Shared by both extraction paths for the same reason as {@link requireRows}.
 */
function finishGrid(grid: SparseGrid): CellTextGrid {
  const rectangular = rectangularize(grid);
  // Every cell claims at least one slot, so a zero width means no row held a
  // cell. There is nothing to convert.
  if (rectangular.length === 0 || rectangular[0].length === 0) {
    throw new TableStructureError("The table has no cells.");
  }
  return rectangular;
}

/**
 * Cell text of a table as a rectangular grid, with `colspan` and `rowspan`
 * expanded (Req 3.1, 3.3, 3.7, 3.8, 3.9).
 *
 * One pass over `table.rows`, which yields `thead` rows first, then `tbody`
 * and direct `tr` rows in tree order, then `tfoot` rows. That is the rendered
 * row order, so the first row is the header record (Req 3.2) even when
 * `tfoot` is authored before `tbody` in the markup — which is why this reads
 * `table.rows` rather than walking the sections itself.
 *
 * A table carrying a header row and zero data rows yields a one-row grid,
 * which serializes to the header record only (Req 6.6). That is not an error.
 *
 * The grid holds exactly one record per table row (Req 3.1): a `rowspan`
 * reaching past the last row is bounded to the rows that exist (Req 3.20), so
 * a one-row table holding a `rowspan="3"` cell yields one record, not three.
 *
 * @throws {TableStructureError} when the table has no rows, or when every row
 * has no cells (Req 7.1).
 */
export function tableToCellTextGrid(table: HTMLTableElement): CellTextGrid {
  const rows = requireRows(table);
  const rowsTotal = rows.length;

  const grid: SparseGrid = [];
  for (let r = 0; r < rowsTotal; r += 1) {
    claimRowSlots(grid, r, rows[r], rowsTotal);
  }

  return finishGrid(grid);
}

// ── Chunked grid extraction ────────────────────────────────────────────────

/** Options for {@link tableToCellTextGridChunked}. */
export interface ChunkedGridOptions {
  /**
   * Table rows processed before control returns to the event loop. The
   * download flow passes {@link CHUNK_ROWS}.
   */
  chunkRows: number;
  /**
   * Called once per completed chunk, and once more at the end, with the
   * cumulative row count and the table's row count (Req 6.4). Counts are
   * monotonic and the last call always reports `rowsTotal`, so a progress
   * indicator driven from this finishes where it says it will.
   */
  onProgress?: (rowsDone: number, rowsTotal: number) => void;
}

/** A positive integer chunk size, falling back to {@link CHUNK_ROWS}. */
function normalizeChunkRows(chunkRows: number): number {
  if (!Number.isFinite(chunkRows)) {
    return CHUNK_ROWS;
  }
  return Math.max(1, Math.floor(chunkRows));
}

/**
 * Hands control back to the event loop between chunks.
 *
 * `setTimeout(..., 0)` is the mechanism because it schedules a *task*, and the
 * browser's rendering opportunity sits between tasks. Three alternatives were
 * rejected:
 *
 *   - `await Promise.resolve()` queues a microtask, and microtasks drain at
 *     the end of the current task, before rendering. The loop would keep
 *     control for the whole conversion and the tab would still freeze, which
 *     is exactly what Req 6.4 is about.
 *   - `requestAnimationFrame` does align with painting, but it is throttled to
 *     the frame rate and stops firing in a hidden tab, so a user who switches
 *     away mid-conversion would come back to an unfinished download.
 *   - `MessageChannel` yields at the right time, but Jest's fake timers do not
 *     control it, so a test could not step the conversion deterministically.
 *
 * `setTimeout` is faked by Jest's timers, so the download-flow test can drive
 * this path under jsdom by advancing them.
 */
function yieldToEventLoop(): Promise<void> {
  return new Promise<void>((resolve) => {
    setTimeout(resolve, 0);
  });
}

/**
 * Same result as {@link tableToCellTextGrid}, built in chunks of `chunkRows`
 * table rows with a yield to the event loop between chunks, so converting a
 * very large table does not freeze the tab and the progress indicator on the
 * activated control can paint (Req 6.4).
 *
 * The grid is identical to the synchronous path's for the same table: both run
 * the same per-row pass under the same row bound (Req 3.20), and the same
 * rectangularization, and both raise {@link TableStructureError} on the same
 * two conditions. Chunking changes only when control returns to the browser,
 * never what comes out.
 *
 * The first yield happens after the first chunk, not before it. The caller is
 * expected to have painted the busy state already — the download flow awaits
 * one animation frame before calling this.
 *
 * @throws {TableStructureError} when the table has no rows, or when every row
 * has no cells (Req 7.1).
 */
export async function tableToCellTextGridChunked(
  table: HTMLTableElement,
  options: ChunkedGridOptions
): Promise<CellTextGrid> {
  const rows = requireRows(table);
  const rowsTotal = rows.length;
  const step = normalizeChunkRows(options.chunkRows);
  const { onProgress } = options;

  const grid: SparseGrid = [];
  for (let r = 0; r < rowsTotal; r += 1) {
    // `table.rows` is a live collection and this loop spans event-loop turns,
    // so a row can be gone by the time the loop reaches it. Stop there rather
    // than throw: what was read so far is still a well-formed grid, and the
    // error path stays reserved for tables that genuinely cannot be laid out.
    const row = rows.item(r);
    if (row === null) {
      break;
    }
    claimRowSlots(grid, r, row, rowsTotal);

    const rowsDone = r + 1;
    if (rowsDone % step === 0 && rowsDone < rowsTotal) {
      onProgress?.(rowsDone, rowsTotal);
      await yieldToEventLoop();
    }
  }

  onProgress?.(rowsTotal, rowsTotal);

  return finishGrid(grid);
}

// ── Row counting ───────────────────────────────────────────────────────────

/**
 * Number of rows the grid treats as header rows: the rows of `table.tHead`
 * when it has any, otherwise the single first row, matching the first grid
 * row that {@link tableToCellTextGrid} emits as the header record.
 */
function headerRowCount(table: HTMLTableElement): number {
  const head = table.tHead;
  if (head !== null && head.rows.length > 0) {
    return head.rows.length;
  }
  return table.rows.length > 0 ? 1 : 0;
}

/**
 * Number of rows of a table that are not header rows (Req 6.1, 6.6).
 *
 * Counts rows straight off the DOM without building the grid, because the
 * download flow calls this on every activation to decide whether to prompt
 * for confirmation.
 */
export function countDataRows(table: HTMLTableElement): number {
  return Math.max(0, table.rows.length - headerRowCount(table));
}
